import {
  CLIENT_EXPORT_SCOPE_TYPE,
  type ClientExportScopeFilters,
  type ClientExportScopeType,
} from '@/app/(main)/client-detail/consts/clientExportScope';
import type { GroupedDownloadPayload } from '@/app/(main)/client-detail/hooks/useGroupedClientDownloadMutation';
import {
  isGroupedExportVariant,
  type DownloadSettingsModalVariant,
} from '@/app/(main)/client-detail/types/downloadSettingsModalVariant';
import type { ExportFormat, FormValues } from '../types';
import {
  mapAddressFromFormValues,
  mapBasicInformationFromFormValues,
  mapGroupViewExcludedFlags,
  mapImportantDatesFromFormValues,
  mapImportantTravelDataFromFormValues,
  mapTravelPreferencesByCategoryFromFormValues,
} from './sharedFormValueMapping';

type BuildGroupedDownloadPayloadParams = {
  clientsId: number[];
  selectedFormat: ExportFormat;
  values: FormValues;
  variant: DownloadSettingsModalVariant;
  scopeType?: ClientExportScopeType;
  scopeFilters?: ClientExportScopeFilters;
};

export function buildGroupedDownloadPayload({
  clientsId,
  selectedFormat,
  values,
  variant,
  scopeType,
  scopeFilters,
}: BuildGroupedDownloadPayloadParams): GroupedDownloadPayload {
  const isGroupView = isGroupedExportVariant(variant);
  const groupViewExcluded = mapGroupViewExcludedFlags(values, isGroupView);
  const isAllScope = scopeType === CLIENT_EXPORT_SCOPE_TYPE.ALL;

  return {
    clients_id: clientsId,
    format: selectedFormat,
    scope_type: scopeType,
    scope_filters: isAllScope ? scopeFilters : undefined,
    include_all: false,
    include_basic_information: mapBasicInformationFromFormValues(values, {
      includeHomeAirports: !isGroupView,
    }),
    include_loyalty_programs: {
      toggle: values.include_loyalty_programs,
      full_name: values.include_loyalty_program_full_name,
      program: values.include_loyalty_program_program,
      number: values.include_loyalty_program_number,
    },
    include_emergency_contact: groupViewExcluded.include_emergency_contact,
    include_address: mapAddressFromFormValues(values, { includeBilling: !isGroupView }),
    include_allergies_dietary_restrictions: values.include_allergies_dietary_restrictions,
    include_important_dates: mapImportantDatesFromFormValues(values),
    include_important_travel_data: mapImportantTravelDataFromFormValues(values),
    include_flight_preferences: values.include_flight_preferences,
    include_travel_preferences_by_category: mapTravelPreferencesByCategoryFromFormValues(values, variant),
    include_related_contacts: groupViewExcluded.include_related_contacts,
    include_notes: groupViewExcluded.include_notes,
    include_tasks: groupViewExcluded.include_tasks,
    include_client_documents: groupViewExcluded.include_client_documents,
    include_passport_details: {
      toggle: values.include_passport_details,
      passport_number: values.include_passport_details_passport_number,
      document_date_of_birth: isGroupView ? false : values.include_passport_details_document_date_of_birth,
      expiry_date: values.include_passport_details_expiry_date,
    },
    include_comfort_related_full_name: values.include_comfort_related_full_name,
    include_comfort_related_height: values.include_comfort_related_height,
    include_comfort_related_weight: values.include_comfort_related_weight,
    include_comfort_related_details: values.include_comfort_related_details,
    include_passport_files_as_separate_pdfs: values.include_passport_files_as_separate_pdfs,
    group_view_single_pdf: isGroupView,
    group_view_tabular: isGroupView && (selectedFormat === 'csv' || selectedFormat === 'spreadsheet'),
  };
}
